import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import AnimatedSection from '../components/common/AnimatedSection';
import { motion } from 'framer-motion';
import { 
  ArrowRight, Calendar, Tag, MapPin, Play, ExternalLink, Filter
} from 'lucide-react';

// Mock news data
const newsData = {
  fr: [
    {
      id: 1,
      date: '10 juillet 2019',
      title: 'Lancement des Groupes de travail Formation-Emploi',
      excerpt: 'Mise en place de groupes thématiques sur les enjeux de formation et d\'emploi.',
      tags: ['Formation', 'Emploi', 'Local CI'],
      themes: ['tech & formation', 'dialogue universités-entreprises', 'orientation', 'professionnalisation'],
      type: 'article'
    },
    {
      id: 2,
      date: '10 juin 2019',
      title: 'Replay du panel : Enjeux économiques et sociaux',
      excerpt: 'Revivez les échanges de notre dernier panel sur les enjeux économiques et sociaux.',
      tags: ['Économie', 'Panel', 'Local CI'],
      type: 'video',
      videoUrl: '#'
    },
    {
      id: 3,
      date: '5 juin 2019',
      title: 'Synthèse du panel économique',
      excerpt: 'Les principales conclusions et recommandations issues de notre panel économique.',
      tags: ['Économie', 'Synthèse', 'Local CI'],
      bullets: [
        'Développement des chaînes de valeur locales',
        'Soutien aux PME/PMI',
        'Formation professionnelle adaptée',
        'Accès équitable aux marchés publics'
      ],
      type: 'article'
    },
  ],
  en: [
    {
      id: 1,
      date: 'July 10, 2019',
      title: 'Launch of Training-Employment Working Groups',
      excerpt: 'Setting up thematic groups on training and employment issues.',
      tags: ['Training', 'Employment', 'Local CI'],
      themes: ['tech & training', 'university-business dialogue', 'orientation', 'professionalization'],
      type: 'article'
    },
    {
      id: 2,
      date: 'June 10, 2019',
      title: 'Panel Replay: Economic and Social Issues',
      excerpt: 'Relive the discussions from our latest panel on economic and social issues.',
      tags: ['Economy', 'Panel', 'Local CI'],
      type: 'video',
      videoUrl: '#'
    },
    {
      id: 3,
      date: 'June 5, 2019',
      title: 'Economic Panel Summary',
      excerpt: 'Key conclusions and recommendations from our economic panel.',
      tags: ['Economy', 'Summary', 'Local CI'],
      bullets: [
        'Development of local value chains',
        'Support for SMEs',
        'Adapted professional training',
        'Fair access to public markets'
      ],
      type: 'article'
    },
  ]
};

const ActualitesPage = () => {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const news = newsData[language] || newsData.fr;
  const [selectedTag, setSelectedTag] = useState(null);

  const allTags = [...new Set(news.flatMap(item => item.tags))];
  const filteredNews = selectedTag 
    ? news.filter(item => item.tags.includes(selectedTag))
    : news;

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className={`py-20 ${isDark ? 'bg-[#1C1917]' : 'bg-gradient-to-br from-[#FAFAF9] to-white'}`}>
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <AnimatedSection>
              <span className="caption mb-4 block">Restez informé</span>
              <h1 className="heading-1 mb-6">
                {language === 'fr' ? 'Actualités' : 'News'}
              </h1>
              <div className="divider mx-auto mb-8" />
            </AnimatedSection>
            
            <AnimatedSection delay={0.2}>
              <p className="body-large max-w-2xl mx-auto">
                {language === 'fr' 
                  ? 'Suivez nos actions, nos réflexions et nos avancées.'
                  : 'Follow our actions, thoughts and progress.'}
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Tags Filter */}
      <section className={`py-6 sticky top-20 z-30 border-b ${
        isDark 
          ? 'bg-[#0C0A09]/90 backdrop-blur-xl border-white/10' 
          : 'bg-white/90 backdrop-blur-xl border-stone-200'
      }`}>
        <div className="container-custom">
          <div className="flex items-center gap-4 overflow-x-auto pb-2 scrollbar-hide">
            <div className="flex items-center gap-2 text-theme-secondary">
              <Filter className="w-4 h-4" />
              <span className="text-sm font-medium whitespace-nowrap">
                {language === 'fr' ? 'Filtrer :' : 'Filter:'}
              </span>
            </div>
            <button
              onClick={() => setSelectedTag(null)}
              className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${
                selectedTag === null
                  ? 'bg-[#5B2D8E] text-white'
                  : isDark
                    ? 'bg-white/10 text-stone-300 hover:bg-white/20'
                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
              }`}
            >
              {language === 'fr' ? 'Tous' : 'All'}
            </button>
            {allTags.map(tag => (
              <button
                key={tag}
                onClick={() => setSelectedTag(tag)}
                className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${
                  selectedTag === tag
                    ? 'bg-[#5B2D8E] text-white'
                    : isDark
                      ? 'bg-white/10 text-stone-300 hover:bg-white/20'
                      : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* News List */}
      <section className={`section-lg ${isDark ? 'bg-[#0C0A09]' : 'bg-[#FAFAF9]'}`}>
        <div className="container-custom">
          <div className="max-w-4xl mx-auto space-y-6">
            {filteredNews.map((item, index) => (
              <AnimatedSection key={item.id} delay={index * 0.1}>
                <motion.article 
                  className={`rounded-2xl overflow-hidden transition-shadow ${
                    isDark 
                      ? 'bg-[#1C1917] border border-white/10 hover:border-[#FFC107]/30' 
                      : 'bg-white border border-stone-100 hover:shadow-lg'
                  }`}
                  whileHover={{ y: -4 }}
                >
                  <div className="p-6 lg:p-8">
                    {/* Meta info */}
                    <div className="flex flex-wrap items-center gap-4 mb-4">
                      <div className="flex items-center gap-2 text-theme-secondary">
                        <Calendar className="w-4 h-4" />
                        <span className="text-sm">{item.date}</span>
                      </div>
                      {item.type === 'video' && (
                        <div className="flex items-center gap-2 text-[#FFC107]">
                          <Play className="w-4 h-4" />
                          <span className="text-sm font-medium">Vidéo</span>
                        </div>
                      )}
                    </div>

                    {/* Title & Excerpt */}
                    <h2 className="text-xl lg:text-2xl font-bold text-theme-primary mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                      {item.title}
                    </h2>
                    <p className="text-theme-secondary mb-4">
                      {item.excerpt}
                    </p>

                    {/* Themes list */}
                    {item.themes && (
                      <div className={`mb-4 pl-4 border-l-2 border-[#FFC107] ${isDark ? 'bg-white/5' : 'bg-stone-50'} p-4 rounded-r-lg`}>
                        <p className="text-sm font-semibold text-[#5B2D8E] dark:text-[#FFC107] mb-2">
                          {language === 'fr' ? 'Thèmes abordés :' : 'Topics covered:'}
                        </p>
                        <ul className="text-sm text-theme-secondary space-y-1">
                          {item.themes.map((theme, i) => (
                            <li key={i}>• {theme}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Bullets */}
                    {item.bullets && (
                      <div className={`mb-4 p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-stone-50'}`}>
                        <ul className="space-y-2">
                          {item.bullets.map((bullet, i) => (
                            <li key={i} className="flex items-start gap-2 text-theme-primary">
                              <span className="text-[#FFC107] font-bold">•</span>
                              {bullet}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {item.tags.map(tag => (
                        <span 
                          key={tag}
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
                            tag === 'Local CI' 
                              ? 'bg-[#FFC107]/20 text-[#FFA000]'
                              : isDark
                                ? 'bg-[#5B2D8E]/30 text-[#7C4DAF]'
                                : 'bg-[#5B2D8E]/10 text-[#5B2D8E]'
                          }`}
                        >
                          <Tag className="w-3 h-3" />
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* Video link */}
                    {item.type === 'video' && item.videoUrl && (
                      <a 
                        href={item.videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#5B2D8E] text-white rounded-full text-sm font-semibold hover:bg-[#7C4DAF] transition-colors"
                      >
                        <Play className="w-4 h-4" />
                        {language === 'fr' ? 'Voir le replay' : 'Watch replay'}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </motion.article>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-[#5B2D8E] to-[#7C4DAF] relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute -top-20 -right-20 w-96 h-96 bg-[#FFC107] rounded-full blur-3xl" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <AnimatedSection>
              <h2 className="heading-2 text-white mb-6">
                {language === 'fr' ? 'Restez informé' : 'Stay informed'}
              </h2>
              <p className="text-xl text-white/80 mb-10">
                {language === 'fr' 
                  ? 'Recevez nos actualités directement dans votre boîte mail.'
                  : 'Receive our news directly in your inbox.'}
              </p>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <Link 
                to="/rejoindre" 
                className="btn-secondary"
              >
                {language === 'fr' ? 'Je veux être informé' : 'Keep me informed'}
                <ArrowRight className="w-5 h-5" />
              </Link>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ActualitesPage;
